import React, { useState } from 'react';
import { calendar } from '../api';

export default function LessonEditModal({ lesson, isOpen, onClose, onSuccess, onDelete }) {
  const [formData, setFormData] = useState({
    title: lesson?.summary || '',
    description: lesson?.description || '',
    location: lesson?.location || '',
    startTime: lesson?.start?.dateTime || '',
    endTime: lesson?.end?.dateTime || ''
  });
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      if (!formData.title?.trim()) {
        setError('Lesson title is required');
        setLoading(false);
        return;
      }

      if (!formData.startTime || !formData.endTime) {
        setError('Start and end times are required');
        setLoading(false);
        return;
      }

      // Validate start < end
      const startDate = new Date(formData.startTime);
      const endDate = new Date(formData.endTime);
      if (startDate >= endDate) {
        setError('Start time must be before end time');
        setLoading(false);
        return;
      }

      // Get user's timezone
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await calendar.updateEvent(lesson.id, {
        title: formData.title,
        description: formData.description,
        location: formData.location,
        startTime: formData.startTime,
        endTime: formData.endTime,
        timezone: userTimeZone  // Send timezone so backend can handle it correctly
      });

      if (!response.data?.event) {
        throw new Error('Invalid response from server: missing event data');
      }

      alert('✅ Lesson updated successfully!');
      onSuccess?.(response.data.event); // Pass updated event
      onClose();
    } catch (err) {
      console.error('❌ Error updating lesson:', err);

      let errorMessage = 'Failed to update lesson';

      if (err.response?.status === 401) {
        errorMessage = 'Session expired. Please login again.';
      } else if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to edit this lesson.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Lesson not found. It may have been deleted.';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.details) {
        errorMessage = `Server error: ${err.response.data.details}`;
      } else if (err.message === 'Network Error') {
        errorMessage = 'Network error. Check your connection.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setError(null);

      const response = await calendar.deleteEvent(lesson.id);

      if (!response.data?.deletedEvent) {
        throw new Error('Invalid response from server: missing deletedEvent data');
      }

      alert('✅ Lesson deleted successfully!');
      onDelete?.(response.data.deletedEvent); // Pass deleted event for undo
      onClose();
    } catch (err) {
      console.error('❌ Error deleting lesson:', err);

      let errorMessage = 'Failed to delete lesson';

      if (err.response?.status === 401) {
        errorMessage = 'Session expired. Please login again.';
      } else if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to delete this lesson.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Lesson not found. It may have already been deleted.';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.details) {
        errorMessage = `Server error: ${err.response.data.details}`;
      } else if (err.message === 'Network Error') {
        errorMessage = 'Network error. Check your connection.';
      }

      setError(errorMessage);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '500px',
        width: '90vw',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        maxHeight: '80vh',
        overflowY: 'auto'
      }}>
        <h2 style={{ marginTop: 0, color: '#2c3e50' }}>✏️ Edit Lesson</h2>

        {error && (
          <div style={{
            padding: '12px',
            background: '#ffe6e6',
            color: '#c0392b',
            borderRadius: '6px',
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Title */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#2c3e50' }}>
              Lesson Title
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              disabled={loading || deleting}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                opacity: loading || deleting ? 0.6 : 1
              }}
              placeholder="e.g., Forehand Technique"
            />
          </div>

          {/* Description */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#2c3e50' }}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              disabled={loading || deleting}
              rows={3}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                opacity: loading || deleting ? 0.6 : 1
              }}
              placeholder="Add lesson details..."
            />
          </div>

          {/* Location */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#2c3e50' }}>
              Location
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              disabled={loading || deleting}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                opacity: loading || deleting ? 0.6 : 1
              }}
              placeholder="e.g., Court 1"
            />
          </div>

          {/* Start Time */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#2c3e50' }}>
              Start Time
            </label>
            <input
              type="datetime-local"
              name="startTime"
              value={formData.startTime.slice(0, 16)}
              onChange={(e) => {
                const dateTime = new Date(e.target.value).toISOString();
                setFormData(prev => ({ ...prev, startTime: dateTime }));
              }}
              disabled={loading || deleting}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                opacity: loading || deleting ? 0.6 : 1
              }}
            />
          </div>

          {/* End Time */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#2c3e50' }}>
              End Time
            </label>
            <input
              type="datetime-local"
              name="endTime"
              value={formData.endTime.slice(0, 16)}
              onChange={(e) => {
                const dateTime = new Date(e.target.value).toISOString();
                setFormData(prev => ({ ...prev, endTime: dateTime }));
              }}
              disabled={loading || deleting}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                opacity: loading || deleting ? 0.6 : 1
              }}
            />
          </div>

          {/* Delete Confirmation */}
          {showDeleteConfirm && (
            <div style={{
              padding: '12px',
              background: '#ffe6e6',
              border: '1px solid #e74c3c',
              borderRadius: '6px',
              color: '#c0392b'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                ⚠️ Are you sure?
              </div>
              <div style={{ fontSize: '14px', marginBottom: '12px' }}>
                This will delete "{formData.title}" from your calendar and notify attendees. This action cannot be undone.
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  style={{
                    flex: 1,
                    padding: '8px',
                    background: '#ecf0f1',
                    color: '#2c3e50',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: deleting ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold',
                    opacity: deleting ? 0.6 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  style={{
                    flex: 1,
                    padding: '8px',
                    background: '#c0392b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: deleting ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold',
                    opacity: deleting ? 0.6 : 1
                  }}
                >
                  {deleting ? '⏳ Deleting...' : '🗑️ Yes, Delete'}
                </button>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
            <button
              onClick={onClose}
              disabled={loading || deleting}
              style={{
                flex: showDeleteConfirm ? 0 : 1,
                minWidth: '100px',
                padding: '10px 16px',
                background: '#ecf0f1',
                color: '#2c3e50',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 'bold',
                cursor: loading || deleting ? 'not-allowed' : 'pointer',
                opacity: loading || deleting ? 0.6 : 1
              }}
            >
              Close
            </button>

            {!showDeleteConfirm && (
              <>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={loading || deleting}
                  style={{
                    flex: 0,
                    padding: '10px 16px',
                    background: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    cursor: loading || deleting ? 'not-allowed' : 'pointer',
                    opacity: loading || deleting ? 0.6 : 1
                  }}
                  title="Delete this lesson"
                >
                  🗑️ Delete
                </button>

                <button
                  onClick={handleSave}
                  disabled={loading || deleting}
                  style={{
                    flex: 1,
                    minWidth: '100px',
                    padding: '10px 16px',
                    background: '#27ae60',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    cursor: loading || deleting ? 'not-allowed' : 'pointer',
                    opacity: loading || deleting ? 0.6 : 1
                  }}
                >
                  {loading ? '⏳ Saving...' : '💾 Save Changes'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
